import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { computePillPlacement, SlotAnchor } from '../utils/pillPlacement';
import BeatsIcon from './icons/BeatsIcon';
import InsertSlotIcon from './icons/InsertSlotIcon';
import PencilIcon from './icons/PencilIcon';
import TrashIcon from './icons/TrashIcon';

const CARET_SIZE = 12;
const ICON_SIZE = 20;
const OUTLINE_WIDTH = 1;
const SEPARATOR_WIDTH = 1;

export interface SlotActionPillProps {
  /** The slot column the pill points at. */
  anchorRef: React.RefObject<HTMLElement | null>;
  canDelete: boolean;
  /** Beats is on: show the beats button between edit and add-after. */
  canSetBeats?: boolean;
  /**
   * Inner slots of a split bar can't take a bar beside them: the button is
   * dimmed but still clickable, so its handler can say why.
   */
  addBeforeDisabled?: boolean;
  addAfterDisabled?: boolean;
  onEdit: () => void;
  onSetBeats?: () => void;
  onAddBefore: () => void;
  onAddAfter: () => void;
  onDelete: () => void;
}

/**
 * Where the chart is visible: the viewport below the fixed header. The pill
 * stays inside it, like the content it's attached to.
 */
const measureAnchor = (node: HTMLElement): SlotAnchor & { hostTop: number } => {
  const hostTop = document.querySelector('header')?.getBoundingClientRect().bottom ?? 0;
  const rect = node.getBoundingClientRect();
  return {
    hostTop,
    x: rect.left,
    y: rect.top - hostTop,
    width: rect.width,
    height: rect.height,
    hostWidth: window.innerWidth,
    hostHeight: window.innerHeight - hostTop,
  };
};

/**
 * Floating action bar for the active slot: add before, edit, [beats,] add
 * after | delete. Mirrors RN's SlotActionPill (minus play — the web app has
 * no chord playback):
 * same neutral language as the row and section controls, plus an outline and
 * shadow because it floats over the chart. Rendered into document.body because
 * rows scroll horizontally and clip anything outside them; it re-measures on
 * any scroll (page or row) and when the slot resizes.
 */
const SlotActionPill: React.FC<SlotActionPillProps> = ({
  anchorRef,
  canDelete,
  canSetBeats = false,
  addBeforeDisabled = false,
  addAfterDisabled = false,
  onEdit,
  onSetBeats,
  onAddBefore,
  onAddAfter,
  onDelete,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [anchor, setAnchor] = useState<ReturnType<typeof measureAnchor> | null>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const isShown = anchor !== null;

  const remeasure = useCallback(() => {
    if (anchorRef.current) setAnchor(measureAnchor(anchorRef.current));
  }, [anchorRef]);

  useLayoutEffect(remeasure, [remeasure]);

  // Fade in once it first appears
  useEffect(() => {
    if (!isShown) return;
    pillRef.current?.animate?.([{ opacity: 0 }, { opacity: 1 }], {
      duration: theme.motion.duration.fast,
      easing: theme.motion.easing.standard,
    });
  }, [isShown, theme.motion.duration.fast, theme.motion.easing.standard]);

  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(remeasure);
    };
    // Capture: row scroll events don't bubble to window
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (anchorRef.current) observer?.observe(anchorRef.current);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [anchorRef, remeasure]);

  const showBeats = canSetBeats && !!onSetBeats;
  const buttonCount = 3 + (showBeats ? 1 : 0) + (canDelete ? 1 : 0);
  const separatorWidth = canDelete ? SEPARATOR_WIDTH + theme.spacing.xs * 2 : 0;
  const pillWidth = theme.spacing.xs * 2 + buttonCount * theme.sizes.hitTarget + separatorWidth;
  const pillHeight = theme.spacing.xs * 2 + theme.sizes.hitTarget;

  const placement = anchor
    ? computePillPlacement(anchor, { width: pillWidth, height: pillHeight }, {
      edge: theme.spacing.sm,
      gap: theme.spacing.sm,
      caretInset: theme.radii.xl + CARET_SIZE / 2,
    })
    : null;
  if (!anchor || !placement) return null;

  const iconColor = theme.colors.textSecondary;
  // Portal events still bubble through the React tree (slot, row, section), so
  // stop them here or a press would also toggle the slot.
  const press = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    action();
  };

  return createPortal(
    <div
      ref={pillRef}
      className="slot-controls"
      data-testid="slot-action-pill"
      onClick={(e) => e.stopPropagation()}
      style={{
        ...styles.pill,
        left: placement.left,
        top: anchor.hostTop + placement.top,
        width: pillWidth,
      }}
    >
      {/* Outline as its own layer so the caret's fill covers it where they join */}
      <div style={styles.outline} />
      {placement.side !== 'overlap' && (
        <div
          style={{
            ...styles.caret,
            left: placement.caretX - CARET_SIZE / 2,
            ...(placement.side === 'above' ? styles.caretBelowPill : styles.caretAbovePill),
          }}
        />
      )}
      <PillButton theme={theme} label="Add slot before" disabled={addBeforeDisabled} onClick={press(onAddBefore)}>
        <InsertSlotIcon position="before" size={ICON_SIZE} color={iconColor} />
      </PillButton>
      <PillButton theme={theme} label="Edit chord" onClick={press(onEdit)}>
        <PencilIcon size={ICON_SIZE - 2} color={iconColor} />
      </PillButton>
      {showBeats && (
        <PillButton theme={theme} label="Set beats" onClick={press(() => onSetBeats?.())}>
          <BeatsIcon size={ICON_SIZE} color={iconColor} />
        </PillButton>
      )}
      <PillButton theme={theme} label="Add slot after" disabled={addAfterDisabled} onClick={press(onAddAfter)}>
        <InsertSlotIcon position="after" size={ICON_SIZE} color={iconColor} />
      </PillButton>
      {canDelete && (
        <>
          <div style={styles.separator} />
          <PillButton theme={theme} label="Delete slot" onClick={press(onDelete)}>
            <TrashIcon size={ICON_SIZE} color={theme.colors.error} />
          </PillButton>
        </>
      )}
    </div>,
    document.body,
  );
};

const PillButton: React.FC<{
  theme: Theme;
  label: string;
  /** Dimmed and announced as disabled, but still clicked through (see addBeforeDisabled). */
  disabled?: boolean;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}> = ({ theme, label, disabled = false, onClick, children }) => {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const overlay = pressed ? theme.opacity.pressed : hovered ? theme.opacity.hover : 0;

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-disabled={disabled || undefined}
      onClick={onClick}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => { setHovered(false); setPressed(false); }}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      style={{
        position: 'relative',
        width: theme.sizes.hitTarget,
        height: theme.sizes.hitTarget,
        flexShrink: 0,
        borderRadius: theme.radii.lg,
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? theme.opacity.disabled : 1,
        backgroundColor: overlay
          ? `color-mix(in srgb, ${theme.colors.text} ${overlay * 100}%, transparent)`
          : 'transparent',
        transition: `background-color ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
      }}
    >
      {children}
    </button>
  );
};

const createStyles = (theme: Theme) => ({
  pill: {
    position: 'fixed' as const,
    // Below the fixed header and modals (50)
    zIndex: 40,
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    boxSizing: 'border-box' as const,
    padding: theme.spacing.xs,
    borderRadius: theme.radii.xl,
    backgroundColor: theme.colors.surfaceSecondary,
    boxShadow: theme.shadows.popover,
  },
  outline: {
    position: 'absolute' as const,
    inset: 0,
    borderRadius: theme.radii.xl,
    border: `${OUTLINE_WIDTH}px solid ${theme.colors.borderSecondary}`,
    pointerEvents: 'none' as const,
  },
  // Rotated square; only its two outward-facing sides carry the outline, and its
  // fill covers the pill's outline where they meet
  caret: {
    position: 'absolute' as const,
    boxSizing: 'border-box' as const,
    width: CARET_SIZE,
    height: CARET_SIZE,
    borderRadius: theme.radii.sm / 2,
    backgroundColor: theme.colors.surfaceSecondary,
    borderStyle: 'solid',
    borderWidth: 0,
    borderColor: theme.colors.borderSecondary,
    transform: 'rotate(45deg)',
    pointerEvents: 'none' as const,
  },
  caretBelowPill: {
    bottom: -CARET_SIZE / 2 + OUTLINE_WIDTH,
    borderRightWidth: OUTLINE_WIDTH,
    borderBottomWidth: OUTLINE_WIDTH,
  },
  caretAbovePill: {
    top: -CARET_SIZE / 2 + OUTLINE_WIDTH,
    borderLeftWidth: OUTLINE_WIDTH,
    borderTopWidth: OUTLINE_WIDTH,
  },
  separator: {
    width: SEPARATOR_WIDTH,
    height: theme.sizes.hitTarget / 2,
    flexShrink: 0,
    marginLeft: theme.spacing.xs,
    marginRight: theme.spacing.xs,
    backgroundColor: theme.colors.borderSecondary,
  },
});

export default SlotActionPill;
